import {Edit2} from 'lucide-react'
import {useI18n} from '@/i18n'
import {Card, CardContent, CardDescription, CardTitle} from '@/components/ui/card'
import {Badge} from '@/components/ui/badge'
import {Button} from '@/components/ui/button'
import {RowCardHeader} from '@/components/shared/RowCardHeader'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {DetailField} from '@/components/shared/DetailField'
import {Stack} from '@/components/shared/Stack'
import {CodeText} from '@/components/shared/CodeText'
import {MutedText} from '@/components/shared/MutedText'
import {Icon} from '@/components/shared/Icon'
import type {SapSfEcInboundConfig} from '@/types/inbound-connector'

interface SapSfEcInboundConfigCardProps {
    config: SapSfEcInboundConfig
    canManage?: boolean
    onEdit?: () => void
}

export function SapSfEcInboundConfigCard({config, canManage, onEdit}: SapSfEcInboundConfigCardProps) {
    const i18n = useI18n()
    return (
        <Card data-testid="sap-sf-ec-config-section" data-help-prefix="sapSfEcInboundConfigCard">
            <RowCardHeader>
                <div>
                    <CardTitle data-testid="card-title-sap-sf-ec-config" data-help-key="_section">
                        {i18n.inboundConnectors.sapSfEc.connectionSection}
                    </CardTitle>
                    <CardDescription>{i18n.inboundConnectors.sapSfEc.connectionDescription}</CardDescription>
                </div>
                {canManage && onEdit && (
                    <Button data-testid="btn-edit-sap-sf-ec-config" size="sm" variant="outline" onClick={onEdit}>
                        <Icon icon={Edit2} className="mr-2"/>
                        {i18n.common.edit}
                    </Button>
                )}
            </RowCardHeader>
            <CardContent>
                <Stack gap="md">
                    <DetailGrid columns={2}>
                        <DetailField label={i18n.inboundConnectors.baseUrl} value={<CodeText>{config.baseUrl}</CodeText>} fullWidth/>
                        <DetailField label={i18n.inboundConnectors.sapSfEc.entitySet} value={<CodeText>{config.entitySet}</CodeText>}/>
                        <DetailField label={i18n.inboundConnectors.sapSfEc.datetimeFormat}
                                     value={<Badge variant="outline">{config.datetimeFormat}</Badge>}/>
                        <DetailField label={i18n.inboundConnectors.sapSfEc.selectFields}
                                     value={<CodeText>{config.selectFieldsCsv || '—'}</CodeText>} fullWidth/>
                        <DetailField label={i18n.inboundConnectors.sapSfEc.expandFields}
                                     value={<CodeText>{config.expandFieldsCsv || '—'}</CodeText>} fullWidth/>
                    </DetailGrid>
                    <DetailGrid columns={2}>
                        <DetailField label={i18n.inboundConnectors.authType}
                                     value={<Badge variant="outline">{config.authType}</Badge>}/>
                        {config.authType === 'OAUTH2_CLIENT' && (<>
                            <DetailField label={i18n.inboundConnectors.clientId} value={config.clientId || '—'}/>
                            <DetailField label={i18n.inboundConnectors.tokenUrl} value={<CodeText>{config.tokenUrl || '—'}</CodeText>}/>
                            <DetailField label={i18n.inboundConnectors.oauthScope} value={config.scope || '—'}/>
                        </>)}
                        {config.authType === 'OAUTH2_SAML_BEARER' && (<>
                            <DetailField label={i18n.inboundConnectors.clientId} value={config.clientId || '—'}/>
                            <DetailField label={i18n.inboundConnectors.tokenUrl} value={<CodeText>{config.tokenUrl || '—'}</CodeText>}/>
                            <DetailField label={i18n.inboundConnectors.sapSfEc.samlIssuer} value={config.samlIssuer || '—'}/>
                            <DetailField label={i18n.inboundConnectors.sapSfEc.samlAudience} value={config.samlAudience || '—'}/>
                            <DetailField label={i18n.inboundConnectors.sapSfEc.samlSubject} value={config.samlSubject || '—'}/>
                        </>)}
                    </DetailGrid>
                    <DetailGrid columns={2}>
                        <DetailField label={i18n.inboundConnectors.pageSize} value={String(config.pageSize)}/>
                        <DetailField label={i18n.inboundConnectors.connectTimeout} value={String(config.connectTimeoutMs)}/>
                        <DetailField label={i18n.inboundConnectors.readTimeout} value={String(config.readTimeoutMs)}/>
                        <DetailField label={i18n.inboundConnectors.maxRetries} value={String(config.maxRetries)}/>
                    </DetailGrid>
                    {config.attributeMappings.length === 0 && (
                        <MutedText>{i18n.inboundConnectors.sapSfEc.noMappings}</MutedText>
                    )}
                </Stack>
            </CardContent>
        </Card>
    )
}
